import React from 'react'
import { formatRelativeTime } from './dashboardUtils'

const NotificationsTab = ({
  notificationFeed,
  markAllNotificationsRead,
  unreadCount,
  onDeclineReassignment,
  onKeepAppointment,
  onNotificationClick,
  processingNotificationId = null,
}) => {
  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-bold text-lg text-gray-900">Notifications</h3>
          <p className="text-sm text-gray-500">Latest updates about your appointments</p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllNotificationsRead}
            disabled={!!processingNotificationId}
            className="text-primary text-sm font-semibold hover:underline px-3 py-1.5 rounded-lg hover:bg-purple-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Mark all read
          </button>
        )}
      </div>
      
      <div className="space-y-4">
        {notificationFeed.length === 0 ? (
          <div className="text-center py-16 px-4">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 mb-4">
              <i className="fas fa-bell-slash text-3xl text-purple-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No notifications yet</h3>
            <p className="text-sm text-gray-500 max-w-sm mx-auto">You'll see updates about your appointments here.</p>
          </div>
        ) : (
          notificationFeed.map(notification => {
            const rawType = notification.rawType || notification.type
            const text = `${notification.title || ''} ${notification.message || ''}`.toLowerCase()
            const source = notification.data?.source
            const looksLikeSalonChange =
              text.includes('reassign') ||
              text.includes('moved your booking') ||
              text.includes('updated by salon') ||
              text.includes('the salon has') ||
              text.includes('salon changed') ||
              text.includes('salon rescheduled')
            const isCancelledType = rawType === 'appointment_cancelled'
            const isAdminChangeCancel =
              !!notification.relatedId &&
              !notification.isRead &&
              !isCancelledType &&
              source !== 'customer' &&
              source !== 'booking' &&
              (
                source === 'admin_adjust_appointment' ||
                notification.data?.allow_customer_cancel === true ||
                rawType === 'appointment_reassignment' ||
                looksLikeSalonChange
              )
            const iconClasses = notification.isRead
              ? 'bg-gray-100 text-gray-400'
              : notification.type === 'booked' || notification.type === 'appointment_booked'
                ? 'bg-purple-100 text-purple-600'
                : notification.type === 'payment' || notification.type === 'payment_success'
                  ? 'bg-green-100 text-green-600'
                  : notification.type === 'favorite' || notification.type === 'favorite_added'
                    ? 'bg-pink-100 text-pink-600'
                    : 'bg-gray-200 text-gray-600'
            
            const icon = notification.type === 'booked' || notification.type === 'appointment_booked'
              ? 'fa-calendar-check'
              : notification.type === 'payment' || notification.type === 'payment_success'
                ? 'fa-credit-card'
                : notification.type === 'favorite' || notification.type === 'favorite_added'
                  ? 'fa-heart'
                  : 'fa-bell'
            
            const titleClasses = notification.isRead ? 'text-gray-500' : 'text-gray-900'
            const messageClasses = notification.isRead ? 'text-gray-500' : 'text-gray-600'
            const isProcessing = processingNotificationId === notification.id
            const actionsDisabled = !!processingNotificationId
            const cardClickable = !!onNotificationClick && !isAdminChangeCancel
            return (
              <div
                key={notification.id}
                role={cardClickable ? 'button' : undefined}
                tabIndex={cardClickable ? 0 : undefined}
                onClick={() => {
                  // Salon change cards: only Keep / Cancel buttons may accept or cancel
                  if (!cardClickable) return
                  onNotificationClick?.(notification)
                }}
                onKeyDown={(e) => {
                  if (!cardClickable) return
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onNotificationClick(notification)
                  }
                }}
                className={`flex items-start gap-3 border border-gray-100 rounded-2xl bg-white p-4 transition-all duration-200 shadow-sm ${
                  cardClickable ? 'cursor-pointer hover:bg-gray-50 hover:shadow-md' : ''
                }`}
              >
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center text-lg flex-shrink-0 ${iconClasses}`}
                >
                  <i className={`fas ${icon}`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <p className={`font-semibold ${titleClasses} break-words`}>{notification.title}</p>
                    {!notification.isRead && <span className="w-2 h-2 rounded-full bg-purple-500 mt-2 flex-shrink-0"></span>}
                  </div>
                  <p className={`text-sm ${messageClasses} break-words mt-1`}>{notification.message}</p>
                  <p className="text-xs text-gray-400 mt-2">{notification.timeLabel || formatRelativeTime(notification.timestamp)}</p>

                  {isAdminChangeCancel && (
                    <div className="mt-3 space-y-2">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={actionsDisabled}
                          onClick={(e) => {
                            e.stopPropagation()
                            onKeepAppointment && onKeepAppointment(notification)
                          }}
                          className="px-3 py-1.5 rounded-lg bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 transition text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Keep Appointment
                        </button>
                        <button
                          type="button"
                          disabled={actionsDisabled}
                          onClick={(e) => {
                            e.stopPropagation()
                            onDeclineReassignment && onDeclineReassignment(notification)
                          }}
                          className="px-3 py-1.5 rounded-lg bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition text-xs font-semibold disabled:opacity-50 disabled:cursor-wait inline-flex items-center gap-1.5"
                        >
                          {isProcessing ? (
                            <>
                              <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                              Please wait…
                            </>
                          ) : (
                            'Cancel Appointment'
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">
                        Keep accepts the salon change. Cancel issues a full refund for card payments.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export default NotificationsTab
